import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { validateStep } from '../../utilities/stepValidation';

import styles from './personalisation-progress-bar.module.css';
/**
 * Custom hook for PersonalisationProgressBar logic and className selection.
 * @param {Object} options - Options object.
 * @param {number} options.totalSteps - Total number of steps.
 * @param {number} options.currentStep - Current step index.
 * @param {boolean} options.isResetting - Whether the progress bar is resetting.
 * @returns {Object} View model for the progress bar component.
 */
export function useViewModel({ currentStep, isResetting }) {
  const { personalisationSteps, personalisationSlots, selections } = usePersonalisationContext();
  const totalSteps = personalisationSteps.size;
  const stepsArray = Array.from({ length: totalSteps });

  /**
   * Get the status of a step.
   * @param {number} index - Step index.
   * @returns {string} Status string.
   */
  const getStepStatus = (index) => {
    // If this is the last step and we're currently on it, check if it's valid.
    if (index === totalSteps - 1 && index === currentStep) {
      const stepIds = Array.from(personalisationSteps?.keys() || []);
      const currentStepId = stepIds[`${currentStep}`];
      const currentStepData = personalisationSteps?.get(currentStepId);

      // Validate the current step.
      const stepValidation = validateStep({
        step: currentStepData,
        personalisationSlots: personalisationSlots || new Map(),
        selections: selections?.slots || []
      });

      // If the last step is valid, show it as completed.
      if (stepValidation.isValid) {
        return 'completed';
      }
    }

    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'current';
    return 'incomplete';
  };

  /**
   * Get the className for a step indicator.
   * @param {string} status - Step status.
   * @returns {string} Class name.
   */
  const stepIndicatorClassName = (status) =>
    `${styles[`personalisation__progress-bar-step-indicator`]} ${styles[`personalisation__progress-bar-step-indicator--${status}`]}`;

  /**
   * Get the className for a line between steps.
   * @param {number} index - Step index.
   * @returns {string} Class name.
   */
  const lineClassName = (index) =>
    `${styles[`personalisation__progress-bar-line`]} ${styles[`personalisation__progress-bar-line--${index < currentStep ? 'completed' : ''}`]}`;

  // Progress bar container className (has logic)
  const progressBarClassName = `${styles[`personalisation__progress-bar`]} ${isResetting ? styles['personalisation__progress-bar--no-transition'] : ''}`;

  return {
    stepsArray,
    getStepStatus,
    stepIndicatorClassName,
    lineClassName,
    progressBarClassName
  };
}
